import React, { useCallback, useState } from 'react';
import { organisationsAPI } from '../../../../organisations/api/organisationsAPI';
import { useActivitySection } from '../../../hooks/useActivitySection';
import ScoreBar from './ScoreBar';
import SectionState from './SectionState';
import { Comments, Composer, usePieceComments } from './PieceComments';
import { plural } from './lib/activityFormat';

const SCORE_HEADINGS = {
  tara_essay: 'Trait levels (0–5)',
  lnat_essay: 'Marking by dimension (0–10)',
};

const Scores = ({ piece }) => (
  <div>
    <div className="td-stat__eyebrow td-act-label">{SCORE_HEADINGS[piece.kind] || 'Scores'}</div>
    {piece.scores.length ? piece.scores.map((s) => (
      <ScoreBar key={s.key} label={s.label} value={s.value} outOf={s.out_of} />
    )) : <p className="td-act-meta">No scores were stored for this piece.</p>}
  </div>
);

/**
 * Feedback, comments and the student's own text for one essay, with a
 * composer that posts a comment on this essay. A posted comment is shown
 * here at once and passed to `onCommented(item, comment)` so the row and
 * the Feedback tab can follow.
 */
const DetailBody = ({ studentId, item, piece, onCommented }) => {
  const thread = usePieceComments(studentId, item, piece.comments, onCommented);
  const [showWork, setShowWork] = useState(false);
  const workId = `writing-${item.kind}-${item.id}-work`;

  return (
    <div className="td-act-detail-grid">
      <Scores piece={piece} />
      <div>
        <div className="td-stat__eyebrow td-act-label">What the student was told</div>
        {piece.feedback ? (
          <>
            <p className="td-act-quote">{piece.feedback.summary}</p>
            {piece.feedback.points.length ? (
              <ul className="td-act-points">
                {piece.feedback.points.map((p) => <li key={p}>{p}</li>)}
              </ul>
            ) : null}
          </>
        ) : <p className="td-act-meta">The feedback stored for this piece cannot be shown.</p>}
        <Comments comments={thread.comments} label="Comments on this piece" />
        <div className="td-act-btnrow">
          {piece.work ? (
            <button
              type="button"
              className="td-btn td-btn--small"
              aria-expanded={showWork}
              aria-controls={workId}
              onClick={() => setShowWork((v) => !v)}
            >
              {`${showWork ? 'Hide' : 'Read'} essay (${plural(piece.work.word_count, 'word')})`}
            </button>
          ) : null}
          {!thread.composing ? (
            <button type="button" className="td-btn td-btn--confirm td-btn--small" onClick={thread.startComposing}>
              Comment on this essay
            </button>
          ) : null}
        </div>
        {thread.composing ? (
          <Composer
            noun="essay"
            posting={thread.posting}
            error={thread.postError}
            onSubmit={thread.post}
            onCancel={thread.cancel}
          />
        ) : null}
        {!piece.work ? <p className="td-act-meta">The essay text is not available.</p> : null}
      </div>
      {piece.work && showWork ? (
        <div id={workId} className="td-act-work">{piece.work.text}</div>
      ) : null}
    </div>
  );
};

/** The open row of the writing table: one essay from GET …/activity/writing/<kind>/<id>. */
const WritingDetail = ({ studentId, item, onCommented }) => {
  const fetchPiece = useCallback(
    (sid) => organisationsAPI.getStudentWritingPiece(sid, item.kind, item.id),
    [item.kind, item.id],
  );
  const state = useActivitySection(fetchPiece, studentId, `${item.kind}:${item.id}`);
  return (
    <>
      <SectionState state={state} what="this essay" />
      {state.status === 'ready' ? (
        <DetailBody studentId={studentId} item={item} piece={state.data} onCommented={onCommented} />
      ) : null}
    </>
  );
};

export default WritingDetail;
